import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { sendClientContact } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import '../styles/ContactClient.css';

const ContactClient = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);
  
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    typeProjet: '',
    budget: '',
    delai: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);

  const typesProjet = [
    { value: 'web', label: t('client.projectType.web') },
    { value: 'mobile', label: t('client.projectType.mobile') },
    { value: 'both', label: t('client.projectType.both') },
  ];

  const budgetRanges = [
    { value: '0-2000', label: t('client.budget.0-2000') },
    { value: '2000-5000', label: t('client.budget.2000-5000') },
    { value: '5000-10000', label: t('client.budget.5000-10000') },
    { value: '10000+', label: t('client.budget.10000+') },
    { value: 'flexible', label: t('client.budget.flexible') }
  ];

  const delaiOptions = [
    { value: 'urgent', label: t('client.deadline.urgent') },
    { value: '1-3mois', label: t('client.deadline.1-3months') },
    { value: '3-6mois', label: t('client.deadline.3-6months') },
    { value: 'flexible', label: t('client.deadline.flexible') }
  ];

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[0-9+\s()-]{10,}$/;
    return re.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nom.trim()) {
      newErrors.nom = t('client.validation.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('client.validation.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('client.validation.emailInvalid');
    }

    if (formData.telephone && !validatePhone(formData.telephone)) {
      newErrors.telephone = t('client.validation.phoneInvalid');
    }

    if (!formData.typeProjet) {
      newErrors.typeProjet = t('client.validation.projectTypeRequired');
    }

    if (!formData.budget) {
      newErrors.budget = t('client.validation.budgetRequired');
    }

    if (!formData.delai) {
      newErrors.delai = t('client.validation.deadlineRequired');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('client.validation.descriptionRequired');
    } else if (formData.description.trim().length < 20) {
      newErrors.description = t('client.validation.descriptionTooShort');
    }

    // Validation du captcha
    if (!captchaToken) {
      newErrors.captcha = t('client.validation.captchaRequired') || 'Veuillez valider le captcha';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    if (errors.captcha) {
      setErrors(prev => ({
        ...prev,
        captcha: ''
      }));
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Envoyer les données avec le token captcha
      const result = await sendClientContact({
        ...formData,
        captchaToken
      });

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          typeProjet: '',
          budget: '',
          delai: '',
          description: ''
        });
        // Reset le captcha
        setCaptchaToken(null);
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
      } else {
        setSubmitStatus('error');
        // Reset le captcha en cas d'erreur
        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
      // Reset le captcha en cas d'erreur
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setCaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-client-container">
      {/* <LanguageSwitcher /> */}
      
      <div className="contact-client-header">
        <h1 className="contact-client-title">{t('client.title')}</h1>
        <p className="contact-client-subtitle">
          {t('client.subtitle')}
        </p>
      </div>

      <form className="contact-form" onSubmit={handleSubmit}>
        {/* Informations personnelles */}
        <div className="form-section">
          <h2 className="form-section-title">{t('client.section.info')}</h2>
          
          <div className="form-group">
            <label htmlFor="nom" className="form-label">
              {t('client.label.name')} <span className="required">{t('common.required')}</span>
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              className={`form-input ${errors.nom ? 'error' : ''}`}
              placeholder={t('client.placeholder.name')}
            />
            {errors.nom && <span className="error-message">{errors.nom}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t('client.label.email')} <span className="required">{t('common.required')}</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder={t('client.placeholder.email')}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="telephone" className="form-label">
                {t('client.label.phone')}
              </label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                className={`form-input ${errors.telephone ? 'error' : ''}`}
                placeholder={t('client.placeholder.phone')}
              />
              {errors.telephone && <span className="error-message">{errors.telephone}</span>}
            </div>
          </div>
        </div>

        {/* Détails du projet */}
        <div className="form-section">
          <h2 className="form-section-title">{t('client.section.project')}</h2>
          
          <div className="form-group">
            <label className="form-label">
              {t('client.label.projectType')} <span className="required">{t('common.required')}</span>
            </label>
            <div className="radio-group">
              {typesProjet.map(type => (
                <label key={type.value} className="radio-label">
                  <input
                    type="radio"
                    name="typeProjet"
                    value={type.value}
                    checked={formData.typeProjet === type.value}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <span className="radio-custom">{type.label}</span>
                </label>
              ))}
            </div>
            {errors.typeProjet && <span className="error-message">{errors.typeProjet}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="budget" className="form-label">
                {t('client.label.budget')} <span className="required">{t('common.required')}</span>
              </label>
              <select
                id="budget"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className={`form-select ${errors.budget ? 'error' : ''}`}
              >
                <option value="">{t('client.placeholder.budget')}</option>
                {budgetRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
              {errors.budget && <span className="error-message">{errors.budget}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="delai" className="form-label">
                {t('client.label.deadline')} <span className="required">{t('common.required')}</span>
              </label>
              <select
                id="delai"
                name="delai"
                value={formData.delai}
                onChange={handleChange}
                className={`form-select ${errors.delai ? 'error' : ''}`}
              >
                <option value="">{t('client.placeholder.deadline')}</option>
                {delaiOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.delai && <span className="error-message">{errors.delai}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              {t('client.label.description')} <span className="required">{t('common.required')}</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder={t('client.placeholder.description')}
              rows="6"
            />
            <div className="textarea-footer">
              <span className="char-count">
                {formData.description.length} / 1000 {t('client.charCount')}
              </span>
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>
          </div>
        </div>

        {/* reCAPTCHA */}
        <div className="form-section">
          <div className="form-group captcha-group">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={handleCaptchaChange}
              onExpired={handleCaptchaExpired}
              hl="fr"
            />
            {errors.captcha && <span className="error-message">{errors.captcha}</span>}
          </div>
        </div>

        {/* Submit status messages */}
        {submitStatus === 'success' && (
          <div className="success-message">
            {t('client.success')} {formData.email}
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="error-message-box">
            {t('client.error')}
          </div>
        )}

        {/* Submit button */}
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              {t('client.button.submitting')}
            </>
          ) : (
            <>
              {t('client.button.submit')}
              <span className="button-arrow">→</span>
            </>
          )}
        </button>

        <p className="form-footer-text">
          {t('client.footer')}
        </p>
      </form>
    </div>
  );
};

export default ContactClient;